<template>
  <b-overlay :show="isLoading">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div class="page-head">
            <h1 class="mb-0">تخفیف‌ها و کدها</h1>
            <div class="page-head__actions">
              <b-button variant="success" @click="renderCreateDiscountModal">
                <feather-icon icon="TrendingDownIcon" size="16" class="mr-50" />
                کاهش قیمت {{ discountSearchType == 2 ? "کلاس" : "دوره" }}
              </b-button>
              <b-button variant="primary" @click="renderCreateCouponModal">
                <feather-icon icon="TagIcon" size="16" class="mr-50" />
                ساخت کد تخفیف
              </b-button>
            </div>
          </div>
          <div class="section-head mt-4">
            <h2 class="mb-0">کاهش قیمت‌ها</h2>
            <b-badge v-if="discountTotalCount" variant="light-success" pill>
              {{ discountTotalCount }} مورد
            </b-badge>
          </div>
          <p class="section-hint">
            قیمت دوره یا کلاس در این بازه برای همه کم می‌شود؛ کاربر لازم نیست کدی وارد کند.
          </p>
          <hr />
          <div class="my-2">
            <!-- Table Top Start -->
            <b-row>
              <b-col cols="12" md="4">
                <b-form-group label="نمایش برای" label-for="discountTarget">
                  <b-form-select
                    id="discountTarget"
                    :options="discountTargets"
                    v-model="discountSearchType"
                  ></b-form-select>
                </b-form-group>
              </b-col>
              <!-- Search -->
              <b-col cols="12" md="4">
                <b-form-group label="از تاریخ" label-for="fromDate">
                  <b-form-input
                    class="fromDate"
                    id="fromDate"
                    placeholder="تاریخ شروع تخفیف را وارد کنید"
                  ></b-form-input>
                  <date-picker
                    v-model="fromDate"
                    class="absolute"
                    customInput=".fromDate"
                    display-format="jYYYY/jMM/jDD"
                    input-format="jYYYY/jMM/jDD"
                    format="YYYY-MM-DD"
                  ></date-picker>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group label="تا تاریخ" label-for="toDate">
                  <b-form-input
                    class="toDate"
                    id="toDate"
                    placeholder="تاریخ پایان تخفیف را وارد کنید"
                  ></b-form-input>
                  <date-picker
                    v-model="toDate"
                    class="absolute"
                    customInput=".toDate"
                    display-format="jYYYY/jMM/jDD"
                    input-format="jYYYY/jMM/jDD"
                    format="YYYY-MM-DD"
                  ></date-picker>
                </b-form-group>
              </b-col>
            </b-row>
            <!-- Table Top End -->
          </div>
          <div class="table-scroll">
          <b-table
              ref="refUserListTable"
              class="position-relative mt-2"
              :items="discountList"
              :fields="discountTableFields"
              primary-key="discountId"
              stacked="md"
              show-empty
              bordered
              striped
              empty-text="کاهش قیمتی برای این بازه ثبت نشده است"
            >
              <template #cell(type)="data">
                {{ discountTypesEnum[data.item.type] }}
              </template>
              <template #cell(value)="data">
                <template v-if="data.item.type == 1">
                  {{ Intl.NumberFormat("en-US").format(data.item.value) }} تومان
                </template>
                <template v-else>{{ data.item.value }}%</template>
              </template>
  
              <template #cell(startDate)="data">
                {{ new Date(data.item.startDate).toLocaleDateString("fa-IR") }}
              </template>
              <template #cell(expireDate)="data">
                {{ new Date(data.item.expireDate).toLocaleDateString("fa-IR") }}
              </template>
              <template #cell(courseTitle)="data">
                <b-link :to="`/courses/${data.item.courseId}`">
                  {{ data.item.courseTitle }}
                </b-link>
              </template>
              <template #cell(lessonTitle)="data">
                <b-link :to="`/lessons/${data.item.lessonId}`">
                  {{ data.item.lessonTitle }}
                </b-link>
              </template>
              <template #cell(actions)="data">
                <div class="d-flex align-items-center">
                  <feather-icon
                    v-b-modal.modal-discount-edit
                    @click="renderEditDiscountModal(data.item)"
                    class="text-primary mr-2 cursor-pointer"
                    icon="EditIcon"
                    size="20"
                  />
                  <feather-icon
                    @click="renderDeleteDiscountModal(data.item)"
                    class="text-danger cursor-pointer"
                    icon="Trash2Icon"
                    size="20"
                  />
                </div>
              </template>
            </b-table>
          </div>
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-center py-2"
          >
            <b-pagination
              v-model="discountPageNumber"
              :total-rows="discountTotalCount"
              :per-page="discountCount"
              first-number
              last-number
              class="mb-0 mt-1"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  style="transform: rotate(180deg)"
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
          <div class="section-head mt-4">
            <h2 class="mb-0">کدهای تخفیف</h2>
            <b-badge v-if="couponTotalCount" variant="light-primary" pill>
              {{ couponTotalCount }} کد
            </b-badge>
          </div>
          <p class="section-hint">کاربر این کدها را در سبد خرید وارد می‌کند.</p>
          <hr />
          <!-- فیلترهای کدها (Coupon/GetAll) -->
          <b-row class="coupon-filters">
            <b-col cols="12" md="3">
              <b-form-group label="جستجوی کد" label-for="couponCodeSearch">
                <b-form-input
                  id="couponCodeSearch"
                  dir="ltr"
                  v-model.trim="couponFilters.code"
                  placeholder="مثلا FM"
                  autocomplete="off"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6" md="3">
              <b-form-group label="وضعیت" label-for="couponActiveFilter">
                <b-form-select
                  id="couponActiveFilter"
                  v-model="couponFilters.isActive"
                  :options="couponActiveOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="6" md="3">
              <b-form-group label="نوع تخفیف" label-for="couponTypeFilter">
                <b-form-select
                  id="couponTypeFilter"
                  v-model="couponFilters.type"
                  :options="couponTypeOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group label="مخصوص" label-for="couponTargetFilter">
                <b-form-select
                  id="couponTargetFilter"
                  v-model="couponFilters.targetType"
                  :options="couponTargetOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="table-scroll">
          <b-table
              class="position-relative mt-2 coupon-table"
              :items="couponList"
              :fields="couponTableCols"
              primary-key="couponId"
              stacked="md"
              show-empty
              bordered
              striped
              empty-text="هنوز کد تخفیفی ساخته نشده است"
            >
              <template #cell(code)="data">
                <div class="d-flex align-items-center">
                  <b-badge
                    variant="light-primary"
                    class="coupon-code"
                    dir="ltr"
                    :title="data.item.code"
                  >
                    {{ data.item.code }}
                  </b-badge>
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    class="btn-icon ml-25"
                    v-b-tooltip.hover="'کپی کد'"
                    :aria-label="'کپی کد ' + data.item.code"
                    @click="copyCouponCode(data.item.code)"
                  >
                    <feather-icon icon="CopyIcon" size="14" />
                  </b-button>
                </div>
                <small class="text-muted">شناسه {{ data.item.couponId }}</small>
              </template>
              <template #cell(value)="data">
                <strong class="text-nowrap">
                  <template v-if="data.item.type == 2">
                    {{ Intl.NumberFormat("en-US").format(data.item.value) }} تومان
                  </template>
                  <template v-else>{{ data.item.value }}٪</template>
                </strong>
                <small class="d-block text-muted">
                  {{ couponTypesEnum[data.item.type] }}
                  <template v-if="data.item.type == 1 && data.item.maxDiscountAmount">
                    ، سقف
                    {{ Intl.NumberFormat("en-US").format(data.item.maxDiscountAmount) }}
                    تومان
                  </template>
                </small>
              </template>
              <template #cell(target)="data">
                <span class="coupon-target-name">{{ couponTargetText(data.item) }}</span>
                <small v-if="data.item.userId" class="d-block text-muted">
                  فقط برای {{ data.item.userName || "یک دانشجو" }}
                </small>
              </template>
              <template #cell(validity)="data">
                <b-badge :variant="couponStatus(data.item).variant" class="mb-25">
                  {{ couponStatus(data.item).text }}
                </b-badge>
                <small class="d-block text-nowrap">
                  {{ new Date(data.item.startDate).toLocaleDateString("fa-IR") }}
                  تا
                  {{ new Date(data.item.endDate).toLocaleDateString("fa-IR") }}
                </small>
              </template>
              <!-- تعداد واقعی استفاده فقط از GetOrdersByCouponId (مودال استفاده‌کنندگان) می‌آید -->
              <template #cell(usage)="data">
                <span class="text-nowrap">
                  {{ data.item.maxUsageCount ? data.item.maxUsageCount + " بار" : "نامحدود" }}
                </span>
                <small class="d-block text-muted text-nowrap">
                  {{ data.item.allowMultipleUsePerUser ? "چند بار برای هر کاربر" : "یک بار برای هر کاربر" }}
                </small>
              </template>
              <template #cell(isActive)="data">
                <b-form-checkbox
                  switch
                  :checked="data.item.isActive"
                  :aria-label="'فعال بودن کد ' + data.item.code"
                  @change="(value) => toggleCouponActive(data.item, value)"
                ></b-form-checkbox>
              </template>
              <template #cell(actions)="data">
                <div class="d-flex align-items-center coupon-actions">
                  <b-button
                    variant="flat-success"
                    size="sm"
                    class="btn-icon"
                    v-b-tooltip.hover="'استفاده‌کنندگان'"
                    aria-label="استفاده‌کنندگان"
                    @click="renderCouponUsageModal(data.item)"
                  >
                    <feather-icon icon="UsersIcon" size="18" />
                  </b-button>
                  <b-button
                    variant="flat-primary"
                    size="sm"
                    class="btn-icon"
                    v-b-tooltip.hover="'ویرایش'"
                    aria-label="ویرایش"
                    @click="renderEditCouponModal(data.item)"
                  >
                    <feather-icon icon="EditIcon" size="18" />
                  </b-button>
                  <b-button
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon"
                    v-b-tooltip.hover="'حذف'"
                    aria-label="حذف"
                    @click="renderDeleteCouponModal(data.item)"
                  >
                    <feather-icon icon="Trash2Icon" size="18" />
                  </b-button>
                </div>
              </template>
            </b-table>
          </div>
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-center py-2"
          >
            <b-pagination
              v-model="couponPageNumber"
              :total-rows="couponTotalCount"
              :per-page="couponCount"
              first-number
              last-number
              class="mb-0 mt-1"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  style="transform: rotate(180deg)"
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-card>
      </b-col>
    </b-row>
    <create-discount-modal
      :key="'discount-' + discountSearchType"
      :discountFor="discountSearchType == 2 ? 'lesson' : 'course'"
      @reFetch="getAllDiscounts"
    ></create-discount-modal>
    <create-coupon-modal
      :editCoupon="tempEditCoupon"
      @reFetch="getAllCoupons"
    ></create-coupon-modal>
    <coupon-usage-modal :coupon="tempUsageCoupon"></coupon-usage-modal>
    <delete-confirmation-modal
      @closeModal="closeDeleteConfirmationModal"
      @deleteDiscount="deleteADiscount"
      @deleteCoupon="deleteACoupon"
      :offerType="tempType"
    ></delete-confirmation-modal>
    <edit-discount-modal
      @reFetch="getAllDiscounts"
      :defaultDiscount="tempDiscount"
    ></edit-discount-modal>
  </b-overlay>
</template>
<script>
import CreateDiscountModal from "@/views/components/Offers/CreateDiscountModal.vue";
import EditDiscountModal from "@/views/components/Offers/EditDiscountModal.vue";
import CreateCouponModal from "@/views/components/Offers/CreateCouponModal.vue";
import CouponUsageModal from "@/views/components/Offers/CouponUsageModal.vue";
import DeleteConfirmationModal from "@/views/components/Offers/DeleteConfirmationModal.vue";
import { GetAllDiscounts, DeleteADiscount } from "@/libs/Api/Discount";
import { GetAllCoupons, DeleteACoupon, EditACoupon } from "@/libs/Api/Coupon";
import { COUPON_TARGET_LABELS } from "@/views/components/Offers/couponTargets";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
  BCard,
  BRow,
  BCol,
  BForm,
  BFormCheckbox,
  BFormRadio,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BModal,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BOverlay,
  BFormSelect,
  VBTooltip,
} from "bootstrap-vue";
export default {
  watch: {
    fromDate: {
      handler(val) {
        this.getAllDiscounts();
      },
    },
    toDate: {
      handler(val) {
        this.getAllDiscounts();
      },
    },
    couponPageNumber: {
      handler(val) {
        this.getAllCoupons();
      },
    },
    // با تغییر فیلتر از صفحه‌ی اول؛ جستجوی متنی با کمی تأخیر
    couponFilters: {
      deep: true,
      handler() {
        clearTimeout(this.couponFilterTimer);
        this.couponFilterTimer = setTimeout(() => {
          if (this.couponPageNumber != 1) {
            this.couponPageNumber = 1;
            return;
          }
          this.getAllCoupons();
        }, 400);
      },
    },
    discountPageNumber: {
      handler(val) {
        this.getAllDiscounts();
      },
    },
    discountSearchType() {
      // اگر صفحه ۱ نبود، watcher شماره صفحه خودش لیست را می‌گیرد
      if (this.discountPageNumber != 1) {
        this.discountPageNumber = 1;
        return;
      }
      this.getAllDiscounts();
    },
  },
  mounted() {
    this.getAllDiscounts();
    this.getAllCoupons();
  },
  data() {
    return {
      isLoading: false,
      // 1: تخفیف‌های دوره، 2: تخفیف‌های کلاس
      discountSearchType: 1,
      discountTargets: [
        { text: "دوره", value: 1 },
        { text: "کلاس", value: 2 },
      ],
      tempUsageCoupon: null,
      // کدی که در فرم ویرایش باز می‌شود؛ null یعنی ساخت کد جدید
      tempEditCoupon: null,
      discountCount: 10,
      discountPageNumber: 1,
      fromDate: null,
      toDate: null,
      discountTotalCount: null,
      discountList: null,
      discountTypesEnum: {
        1: "مبلغ",
        2: "درصد",
      },
      couponTypesEnum: {
        1: "درصد",
        2: "مبلغ",
      },
      discountTableCols: [
        {
          key: "discountId",
          label: "شناسه",
        },
        {
          key: "type",
          label: "نوع",
        },
        {
          key: "value",
          label: "مقدار",
        },
        {
          key: "startDate",
          label: "تاریخ شروع",
        },
        {
          key: "expireDate",
          label: "تاریخ پایان",
        },
        {
          key: "courseTitle",
          label: "نام دوره",
        },
        {
          key: "actions",
          label: "عملیات",
        },
      ],
      couponTableCols: [
        { key: "code", label: "کد تخفیف" },
        { key: "value", label: "تخفیف" },
        { key: "target", label: "مخصوص" },
        { key: "validity", label: "اعتبار" },
        { key: "usage", label: "سقف استفاده" },
        { key: "isActive", label: "فعال" },
        { key: "actions", label: "عملیات" },
      ],
      couponCount: 10,
      couponPageNumber: 1,
      couponTotalCount: null,
      couponFilters: {
        code: "",
        isActive: null,
        type: null,
        targetType: null,
      },
      couponFilterTimer: null,
      couponActiveOptions: [
        { text: "همه", value: null },
        { text: "فعال", value: true },
        { text: "غیرفعال", value: false },
      ],
      couponTypeOptions: [
        { text: "همه", value: null },
        { text: "درصدی", value: 1 },
        { text: "مبلغ ثابت", value: 2 },
      ],
      couponTargetOptions: [
        { text: "همه", value: null },
        { text: "همه‌ی موارد", value: 1 },
        { text: "پکیج", value: 4 },
        { text: "درس", value: 3 },
        { text: "استاد خصوصی", value: 2 },
        { text: "پشتیبانی ساعتی", value: 5 },
      ],
      couponUserId: null,
      couponList: null,
      tempItem: null,
      tempType: null,
      tempDiscount: null,
    };
  },
  methods: {
    renderEditDiscountModal(item) {
      this.tempDiscount = item;
      this.$bvModal.show("modal-discount-edit");
    },
    couponTargetText(coupon) {
      const label = COUPON_TARGET_LABELS[coupon.targetType];
      if (!label) return `نوع نامعتبر (${coupon.targetType})`;
      // نوع ۱ همیشه «همه» است؛ بقیه برای یک مورد خاص
      if (coupon.targetType == 1 || !coupon.targetValue) return label;
      return `${label}: ${coupon.targetName || "#" + coupon.targetValue}`;
    },
    // وضعیت فقط برای نمایش؛ تصمیم نهایی با بکند است
    couponStatus(coupon) {
      const now = Date.now();
      if (!coupon.isActive) {
        return { text: "غیرفعال", variant: "light-secondary" };
      }
      if (coupon.endDate && new Date(coupon.endDate).getTime() < now) {
        return { text: "منقضی", variant: "light-secondary" };
      }
      if (coupon.startDate && new Date(coupon.startDate).getTime() > now) {
        return { text: "شروع نشده", variant: "light-info" };
      }
      return { text: "فعال", variant: "light-success" };
    },
    async copyCouponCode(code) {
      try {
        await navigator.clipboard.writeText(code);
        this.$toast({
          component: ToastificationContent,
          props: { title: "کد کپی شد", icon: "CopyIcon", variant: "success" },
        });
      } catch (error) {
        console.error(error);
      }
    },
    // روشن/خاموش با Update و همان فیلدهای خود کد (فیلدهای خالی در خروجی بکند نمی‌آیند)
    async toggleCouponActive(coupon, isActive) {
      let _this = this;
      let isSaved = false;
      try {
        // eslint-disable-next-line no-unused-vars
        const { orders, userName, targetName, ...fields } = coupon;
        let editCoupon = new EditACoupon(_this);
        editCoupon.setRequestParamDataObj({ ...fields, isActive });
        await editCoupon.fetch((response) => {
          if (response && response.isSuccess) {
            isSaved = true;
            coupon.isActive = isActive;
            _this.$toast({
              component: ToastificationContent,
              props: {
                title: isActive ? "کد تخفیف فعال شد" : "کد تخفیف غیرفعال شد",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          }
        });
      } catch (error) {
        console.error(error);
      }
      // اگر ذخیره نشد، کلید به وضعیت واقعی سرور برمی‌گردد
      if (!isSaved) this.getAllCoupons();
    },
    async offerDeactivateInstead(coupon) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `کد ${coupon.code} قبلاً در سفارش‌ها استفاده شده و برای حفظ تاریخچه‌ی خریدها قابل حذف نیست. به‌جای حذف، غیرفعالش کنیم تا دیگر کسی نتواند از آن استفاده کند؟`,
        {
          title: "این کد قابل حذف نیست",
          okTitle: "غیرفعال کن",
          cancelTitle: "انصراف",
          okVariant: "primary",
          centered: true,
        }
      );
      if (confirmed) this.toggleCouponActive(coupon, false);
    },
    renderCouponUsageModal(item) {
      this.tempUsageCoupon = item;
      this.$bvModal.show("modal-coupon-usage");
    },
    closeDeleteConfirmationModal() {
      this.$bvModal.hide("modal-item-delete");
      this.tempItem = null;
      this.tempType = null;
    },
    renderDeleteDiscountModal(item) {
      this.tempType = 1;
      this.tempItem = item;
      this.$bvModal.show("modal-item-delete");
    },
    renderDeleteCouponModal(item) {
      this.tempType = 2;
      this.tempItem = item;
      this.$bvModal.show("modal-item-delete");
    },
    renderCreateDiscountModal() {
      this.$bvModal.show("modal-discount-add");
    },
    renderCreateCouponModal() {
      this.tempEditCoupon = null;
      this.$nextTick(() => this.$bvModal.show("modal-coupon-add"));
    },
    renderEditCouponModal(item) {
      this.tempEditCoupon = item;
      this.$nextTick(() => this.$bvModal.show("modal-coupon-add"));
    },
    async deleteADiscount() {
      try {
        this.isLoading = true;
        let _this = this;
        let deleteADiscount = new DeleteADiscount(_this);
        deleteADiscount.setRequestParam({ id: _this.tempItem.discountId });
        await deleteADiscount.fetch((response) => {
          if (response.isSuccess) {
            _this.$toast({
              component: ToastificationContent,
              props: {
                title: "کاهش قیمت حذف شد",
                icon: "CheckIcon",
                variant: "success",
              },
            });
            _this.closeDeleteConfirmationModal();
            _this.getAllDiscounts();
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async deleteACoupon() {
      try {
        this.isLoading = true;
        let _this = this;
        let deleteCoupon = new DeleteACoupon(_this);
        deleteCoupon.setCouponId(_this.tempItem.couponId);
        await deleteCoupon.fetch((response) => {
          if (response && response.isSuccess === true) {
            _this.$toast({
              component: ToastificationContent,
              props: {
                title: "کد تخفیف حذف شد",
                icon: "CheckIcon",
                variant: "success",
              },
            });
            _this.closeDeleteConfirmationModal();
            _this.getAllCoupons();
          } else {
            // خطا بی‌صدا نماند (مثلاً ۴۰۰ بکند)
            const errors = response && response.errors;
            const message =
              typeof errors === "string"
                ? errors
                : errors && typeof errors === "object"
                ? String(Object.values(errors).flat()[0] || "")
                : "";
            const coupon = _this.tempItem;
            _this.closeDeleteConfirmationModal();
            // بکند کد استفاده‌شده را حذف نمی‌کند تا تاریخچه‌ی سفارش‌ها نشکند؛ غیرفعال کردن پیشنهاد می‌شود
            if (coupon && coupon.isActive && message.includes("استفاده")) {
              _this.offerDeactivateInstead(coupon);
            } else {
              _this.$toast({
                component: ToastificationContent,
                props: {
                  title: message || "حذف کد تخفیف انجام نشد",
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            }
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getAllDiscounts() {
      try {
        this.isLoading = true;
        let _this = this;
        let getAllDiscounts = new GetAllDiscounts(_this);
        let qParams = {
          count: _this.discountCount,
          pageNumber: _this.discountPageNumber,
          searchType: _this.discountSearchType,
        };
        if (this.fromDate) {
          qParams["fromDate"] = this.fromDate;
        }
        if (this.toDate) {
          qParams["toDate"] = this.toDate;
        }
        getAllDiscounts.setRequestParam(qParams);
        await getAllDiscounts.fetch((response) => {
          if (response.isSuccess) {
            _this.discountTotalCount = response.data.discountsCount;
            _this.discountList = response.data.discounts;
          }
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getAllCoupons() {
      try {
        this.isLoading = true;
        let _this = this;
        let getAllCoupons = new GetAllCoupons(_this);
        let qParams = {
          count: _this.couponCount,
          pageNumber: _this.couponPageNumber,
        };
        const filters = this.couponFilters;
        if (filters.code) qParams["code"] = filters.code;
        if (filters.isActive !== null) qParams["isActive"] = filters.isActive;
        if (filters.type) qParams["type"] = filters.type;
        if (filters.targetType) qParams["targetType"] = filters.targetType;
        getAllCoupons.setRequestParam(qParams);
        await getAllCoupons.get(
          (response) => {
            if (response && response.isSuccess && response.data) {
              _this.couponList = response.data.coupons;
              _this.couponTotalCount = response.data.couponsCount;
            }
          },
          () => {
            _this.couponList = [];
          }
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  computed: {
    discountTableFields() {
      const titleCol =
        this.discountSearchType == 2
          ? { key: "lessonTitle", label: "نام کلاس" }
          : { key: "courseTitle", label: "نام دوره" };
      return this.discountTableCols.map((col) =>
        col.key == "courseTitle" ? titleCol : col
      );
    },
  },
  directives: {
    BTooltip: VBTooltip,
  },
  components: {
    CouponUsageModal,
    BFormSelect,
    CreateDiscountModal,
    EditDiscountModal,
    CreateCouponModal,
    DeleteConfirmationModal,
    BCard,
    BRow,
    BCol,
    BForm,
    BFormCheckbox,
    BFormRadio,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BModal,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BOverlay,
  },
};
</script>

<style scoped>
.page-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.page-head__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.section-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-hint {
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
  color: #6e6b7b;
}

/* کدهای بلند ستون را پهن نکنند؛ متن کامل در title و با دکمه‌ی کپی در دسترس است */
.coupon-code {
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: monospace;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
}

/* اسم استاد/درس/پکیج بلند باشد، به‌جای پهن کردن جدول می‌شکند */
.coupon-target-name {
  display: block;
  max-width: 11rem;
}

.coupon-actions {
  gap: 0.25rem;
}

/* در عرض‌های میانی جدول داخل کارت اسکرول می‌خورد، نه کل صفحه */
.table-scroll {
  overflow-x: auto;
}

/* فاصله‌ی پیش‌فرض قالب (۲rem دو طرف) برای ۷ ستون زیاد است؛ با این مقدار جدول در دسکتاپ بدون اسکرول جا می‌شود */
.coupon-table ::v-deep th,
.coupon-table ::v-deep td {
  padding: 0.7rem 0.6rem;
  vertical-align: middle;
}

.coupon-table ::v-deep th {
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .page-head__actions,
  .page-head__actions .btn {
    width: 100%;
  }
}
</style>
